import { DataService } from "@/services/data";
import { GraphView } from "@/types/dialer/graph-view";
import { SimulationRun } from "@/types/dialer/simulation";
import { handle_dialer_error } from "@/utils/dialer-errors";
import { handle_success } from "@/utils/wretch";
import { RouteObject } from "react-router-dom";
import { resolveVersion } from "./resolve";

async function loadRun(runId: string): Promise<SimulationRun> {
    return (await DataService.client.url(`/dialer/simulations/${runId}/`).get().json()) as SimulationRun;
}

async function loadView(versionId: number): Promise<GraphView> {
    return (await DataService.client.url(`/dialer/workflow-versions/${versionId}/view/`).get().json()) as GraphView;
}

/**
 * Every step posts here and gets the complete run state back, never a delta: that is
 * what makes the screen unable to drift away from the interpreter.
 */
async function applyStep(runId: string | undefined, action: string | null, data: any) {
    if (action === "create") {
        return DataService.client.url(`/dialer/simulations/`).post(data).json();
    }
    if (action === "inject") {
        return DataService.client.url(`/dialer/simulations/${runId}/inject/`).post(data).json();
    }
    if (action === "advance-clock") {
        return DataService.client.url(`/dialer/simulations/${runId}/advance-clock/`).post(data).json();
    }
    if (action === "snapshot") {
        return DataService.client.url(`/dialer/simulations/${runId}/snapshot/`).patch(data).json();
    }
    if (action === "save-scenario") {
        return DataService.client.url(`/dialer/simulations/${runId}/save-scenario/`).post(data).json();
    }
    throw new Error(`Unknown simulator action “${action}”.`);
}

const SimulatorRoutes: RouteObject[] = [
    {
        path: "workflows/:code/v/:version/simulateur",
        loader: async ({ params }) => {
            const { version } = await resolveVersion(params.code as string, params.version);
            return { view: await loadView(version.id), run: null };
        },
        action: async ({ request }) => {
            const action = new URL(request.url).searchParams.get("action");
            try {
                return handle_success(await applyStep(undefined, action, await request.json()));
            } catch (error) {
                return handle_dialer_error(error);
            }
        },
        async lazy() {
            const { DialerSimulatorPage } = await import("@/pages/dialer/simulator/run");
            return { Component: DialerSimulatorPage };
        }
    },
    {
        path: "workflows/:code/v/:version/simulateur/:run_id",
        loader: async ({ params }) => {
            const { version } = await resolveVersion(params.code as string, params.version);
            const [view, run] = await Promise.all([loadView(version.id), loadRun(params.run_id as string)]);
            return { view, run };
        },
        action: async ({ params, request }) => {
            const action = new URL(request.url).searchParams.get("action");
            try {
                return handle_success(await applyStep(params.run_id, action, await request.json()));
            } catch (error) {
                return handle_dialer_error(error);
            }
        },
        async lazy() {
            const { DialerSimulatorPage } = await import("@/pages/dialer/simulator/run");
            return { Component: DialerSimulatorPage };
        }
    }
];

export default SimulatorRoutes;
